import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useCategoryMangment } from "@services"
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
} from "@mui/material"

interface IProps {
  selectedCategoryIds: number[]
  onPropertyIds: (propertyIds: number[]) => void
  onCategoryChange: (categoryIds: number[]) => void
}

function CategoryDetailFetcher({
  categoryId,
  onIds,
}: {
  categoryId: number
  onIds: (id: number, propertyIds: number[]) => void
}) {
  const { GetCategoryByIdQuery } = useCategoryMangment()
  const { data } = GetCategoryByIdQuery(categoryId.toString())
  useEffect(() => {
    const result = data?.data?.result
    if (!result?.length) return
    const ids = (result[0]?.properties ?? []).map((p: { propertyId: number }) => p.propertyId)
    onIds(categoryId, ids)
  }, [data, categoryId])
  return null
}

export default function CategoryFilter({ selectedCategoryIds, onPropertyIds, onCategoryChange }: IProps) {
  const { t, i18n: { language } } = useTranslation()
  const { GetCategoriesQuery } = useCategoryMangment()

  const { data: categoriesData } = GetCategoriesQuery({
    pageNumber: "1",
    pageSize: "100",
  })

  const categories = categoriesData?.data?.result ?? []

  // Map of categoryId -> propertyIds collected from detail fetchers
  const propertyIdMap = useMemo(() => new Map<number, number[]>(), [])

  const handleIdsFromFetcher = (catId: number, ids: number[]) => {
    propertyIdMap.set(catId, ids)
    // Merge all selected category propertyIds
    const merged = Array.from(
      new Set(
        selectedCategoryIds.flatMap((id) => propertyIdMap.get(id) ?? [])
      )
    )
    onPropertyIds(merged)
  }

  // Recalculate merged IDs whenever selection changes (handles deselection)
  useEffect(() => {
    if (selectedCategoryIds.length === 0) {
      onPropertyIds([])
      return
    }
    const merged = Array.from(
      new Set(selectedCategoryIds.flatMap((id) => propertyIdMap.get(id) ?? []))
    )
    onPropertyIds(merged)
  }, [selectedCategoryIds])

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[]
    onCategoryChange(value)
    if (value.length === 0) onPropertyIds([])
  }

  const isRtl = language === "ar"

  const renderValue = (selected: number[]) => {
    if (selected.length === 0) return t("FINANCE.ALL_CATEGORIES")
    return selected
      .map((id) => categories.find((c) => c.categoryId === id)?.categoryName ?? "")
      .filter(Boolean)
      .join(", ")
  }

  return (
    <>
      {selectedCategoryIds.map((catId) => (
        <CategoryDetailFetcher
          key={catId}
          categoryId={catId}
          onIds={handleIdsFromFetcher}
        />
      ))}
      <Select
        multiple
        displayEmpty
        value={selectedCategoryIds}
        onChange={handleChange}
        input={<OutlinedInput />}
        renderValue={renderValue}
        size="small"
        sx={{
          minWidth: 180,
          fontSize: 13,
          fontWeight: 500,
          background: '#fff',
          borderRadius: '8px',
          direction: isRtl ? 'rtl' : 'ltr',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d9d0ff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#5b3fff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#5b3fff' },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '12px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
              mt: 0.5,
              direction: isRtl ? 'rtl' : 'ltr',
            },
          },
        }}
      >
        {categories.map((cat) => (
          <MenuItem key={cat.categoryId} value={cat.categoryId} sx={{ gap: 1, direction: isRtl ? 'rtl' : 'ltr' }}>
            <Checkbox
              checked={selectedCategoryIds.includes(cat.categoryId)}
              size="small"
              sx={{ '&.Mui-checked': { color: '#5b3fff' } }}
            />
            <ListItemText
              primary={cat.categoryName}
              primaryTypographyProps={{ fontSize: 13, fontWeight: 500, color: '#435363' }}
            />
          </MenuItem>
        ))}
      </Select>
    </>
  )
}
